/**
 * Planet drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const planetDrawingsPack: ToilePack = {
    slug: 'planet-drawings',
    category: 'illustration',
    name: 'Planet drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'saturn', label: 'Saturn', subject: 'Saturn with its rings tilted, the bands on its surface shaded and two small moons nearby'},
        {key: 'earthrise', label: 'Earthrise', subject: "the Earth rising over the Moon's cratered horizon, clouds and oceans on its face"},
        {key: 'drift', label: 'Ringed planet', subject: 'a small ringed planet drifting through a field of stars, an asteroid belt curving past it'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, smooth spherical shading, terminator shadow and fine surface detail', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'stipple', label: 'Stipple ink', style: 'stipple ink drawing, tone built from thousands of dots, a round volume lit from one side', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'starfield', label: 'White on black', style: 'white pencil and chalk drawing on black paper, glowing limb, scattered stars', palette: 'white on black', framing: 'on a sheet of black paper, the planet centered, a thin black edge'},
    ],
    directions: ['Mars', 'Jupiter', 'a comet'],
    directionHint: 'try: Mars',
    seo: {
        title: 'Planet Drawings: Saturn, Earthrise, Ringed Planet',
        metaDescription: 'Nine planet drawings: Saturn with its rings, the Earth rising over the Moon and a ringed planet in a star field, shaded in three media.',
        h1: 'Planet drawings: Saturn, Earthrise, a ringed planet in a star field',
        tagline: 'Three space scenes, each drawn in graphite pencil, stipple ink, and white on black.',
        intro: "Three planet drawings: Saturn with its tilted rings and bands, the Earth rising over the Moon's cratered edge, and a small ringed planet drifting through a field of stars. Each planet is shaded to look round, in graphite, in stipple ink and in white on black paper. Good for a school space project or a wall print. Type a different planet or a twist, such as Mars or a comet, and the same media carry it.",
        useCases: [
            'a school space project',
            "a wall print for a child's room",
            'a sketchbook study of shading on a sphere',
            'a space-themed card',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three planet scenes: Saturn with its rings, the Earth rising over the Moon, and a ringed planet among stars. Each scene appears in three media.'},
            {q: 'Can I ask for a different planet?', a: 'Yes. Type a planet or twist, such as Mars, Jupiter or a comet. A typed direction is a request, and the drawing may differ from the samples.'},
            {q: 'How do the three styles differ?', a: 'Pencil uses smooth spherical shading with a shadow line where the light ends. Stipple ink builds tone from many dots on a round form lit from one side. White on black gives a glowing edge over dark paper.'},
            {q: 'Is each drawing square?', a: 'Yes. The planet drawings are square.'},
        ],
    },
}
